import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Clock, Loader2, X, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import type { SplitPaymentData } from '@/hooks/useSplitPaymentStatus';

interface SplitPaymentStatusCardProps {
  splitPayment: SplitPaymentData;
  onCancelled: () => void;
  onPayPartner?: (splitPayment: SplitPaymentData) => void;
  onRefresh?: () => void;
}

const SplitPaymentStatusCard = ({ splitPayment, onCancelled, onPayPartner, onRefresh }: SplitPaymentStatusCardProps) => {
  const { language } = useLanguage();
  const [cancelling, setCancelling] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyFailed, setVerifyFailed] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');

  const getInitials = (name?: string | null) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Countdown timer
  useEffect(() => {
    const updateTimer = () => {
      const now = new Date().getTime();
      const expires = new Date(splitPayment.expires_at).getTime();
      const diff = expires - now;

      if (diff <= 0) {
        setTimeLeft(language === 'es' ? 'Expirado' : 'Expired');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setTimeLeft(`${minutes}m ${seconds}s`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [splitPayment.expires_at, language]);

  // El hold del pagador solo está confirmado si payer_payment_intent_id está guardado
  const payerHoldConfirmed = !!splitPayment.payer_payment_intent_id;

  // Only payer can cancel
  const canCancel = splitPayment.userRole === 'payer';

  const handleVerifyPayerPayment = async () => {
    if (!splitPayment.payer_checkout_session_id) {
      setVerifyFailed(true);
      return;
    }
    setVerifying(true);
    setVerifyFailed(false);
    try {
      const { data, error } = await supabase.functions.invoke('verify-payment-v3', {
        body: { sessionId: splitPayment.payer_checkout_session_id }
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (onRefresh) onRefresh();
    } catch (err: any) {
      console.error('Error verifying payer payment:', err);
      setVerifyFailed(true);
    } finally {
      setVerifying(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const { data, error } = await supabase.functions.invoke('cancel-split-payment', {
        body: { splitPaymentId: splitPayment.id },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      onCancelled();
    } catch (err: any) {
      console.error('Error cancelling split payment:', err);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Card className="bg-white border-primary/20">
      <CardContent className="p-4 space-y-4">
        {/* Title */}
        <p className="text-sm font-semibold text-center text-foreground">
          {language === 'es' ? 'Estado de Pago Separado' : 'Split Payment Status'}
        </p>

        {/* Players row */}
        <div className="flex items-center justify-around">
          {/* Payer */}
          <div className="flex flex-col items-center gap-1.5">
            <Avatar className="h-12 w-12 ring-2 ring-green-500/30">
              <AvatarImage src={splitPayment.payer_profile?.avatar_url || undefined} />
              <AvatarFallback className="bg-gradient-to-br from-primary to-primary/70 text-primary-foreground font-bold text-sm">
                {getInitials(splitPayment.payer_profile?.full_name)}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs font-medium text-foreground truncate max-w-[100px] text-center">
              {splitPayment.payer_profile?.full_name || (language === 'es' ? 'Tú' : 'You')}
            </span>
            {payerHoldConfirmed ? (
              <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px] px-2 py-0">
                <CheckCircle className="h-3 w-3 mr-0.5" />
                {language === 'es' ? 'Confirmado' : 'Confirmed'}
              </Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] px-2 py-0">
                <AlertCircle className="h-3 w-3 mr-0.5" />
                {language === 'es' ? 'Pago pendiente' : 'Payment pending'}
              </Badge>
            )}
          </div>

          {/* Divider */}
          <div className="h-12 w-px bg-border" />

          {/* Partner */}
          <div className="flex flex-col items-center gap-1.5">
            <Avatar className="h-12 w-12 ring-2 ring-amber-500/30">
              <AvatarImage src={splitPayment.partner_profile?.avatar_url || undefined} />
              <AvatarFallback className="bg-gradient-to-br from-amber-400 to-amber-600 text-white font-bold text-sm">
                {getInitials(splitPayment.partner_profile?.full_name)}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs font-medium text-foreground truncate max-w-[100px] text-center">
              {splitPayment.partner_profile?.full_name || (language === 'es' ? 'Compañero' : 'Partner')}
            </span>
            <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] px-2 py-0">
              <Clock className="h-3 w-3 mr-0.5" />
              {language === 'es' ? 'Pendiente' : 'Pending'}
            </Badge>
          </div>
        </div>

        {/* Countdown */}
        <div className="flex items-center justify-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">
            {language === 'es' ? 'Tiempo restante:' : 'Time remaining:'}
          </span>
          <span className="font-bold text-foreground">{timeLeft}</span>
        </div>

        {/* Pay button - only for partner, only if payer hold is confirmed */}
        {splitPayment.userRole === 'partner' && onPayPartner && (
          <Button
            size="sm"
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
            onClick={() => onPayPartner(splitPayment)}
            disabled={!payerHoldConfirmed}
          >
            {language === 'es'
              ? `Pagar $${splitPayment.partner_amount?.toFixed(2) || '0.00'}`
              : `Pay $${splitPayment.partner_amount?.toFixed(2) || '0.00'}`}
          </Button>
        )}

        {/* Payer: payment not confirmed yet — show options */}
        {splitPayment.userRole === 'payer' && !payerHoldConfirmed && (
          <div className="space-y-2">
            <Button
              size="sm"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              onClick={handleVerifyPayerPayment}
              disabled={verifying || cancelling}
            >
              {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {language === 'es' ? 'Ya realicé mi pago' : 'I already paid'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="w-full border-destructive/30 text-destructive hover:bg-destructive/5 font-semibold"
              onClick={handleCancel}
              disabled={cancelling || verifying}
            >
              {cancelling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <X className="h-4 w-4 mr-2" />}
              {language === 'es' ? 'No realicé mi pago' : 'I did not pay'}
            </Button>
          </div>
        )}

        {/* Cancel button - only for payer when payment IS confirmed (waiting for partner) */}
        {canCancel && payerHoldConfirmed && (
          <Button
            variant="outline"
            size="sm"
            className="w-full border-destructive/30 text-destructive hover:bg-destructive/5"
            onClick={handleCancel}
            disabled={cancelling}
          >
            {cancelling ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <X className="h-4 w-4 mr-2" />
            )}
            {language === 'es' ? 'Cancelar inscripción' : 'Cancel registration'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default SplitPaymentStatusCard;
